import { useQueries } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { longRelativeTime, shortRelativeTime } from '../../../lib/contact-rows';
import { useTranslation } from '../../../lib/i18n';
import { profileLinksApi } from '../../../lib/profile-links-api';
import { useProfileLinksEnabled } from '../../../lib/profile-links-flag';
import {
  leadDisplayName,
  leadOpenTarget,
  leadRowStatus,
  leadSource,
  peopleLeadLinks,
  peopleLeadRows,
  sortLeadRowsForSection,
  type PeopleLeadRow,
} from '../../../lib/profile-links-model';
import { queryKeys } from '../../../lib/query-client';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useProfileLinksList } from '../../../lib/use-profile-links';
import { Avatar, Card, Typography } from '../ui';
import { CategoryList, CategoryListGroupHeader, useCategoryRowClass, useCategoryTitleClass } from '../category-view/CategoryView';
import { StageDot } from '../contacts/StageDot';
import styles from '../contacts/contacts.module.css';

/**
 * People's lead rows — every read link's leads, newest first (`peopleLeadRows`) — or `[]` while the spec is off,
 * loading, or failed. The Contacts list reads them (`ContactsBody`'s `profileLeads`) so a lead who is already a
 * contact is folded into that contact's row instead of being listed twice (`matchLeadsToContacts`).
 *
 * Behind `symphonyProfileLinks` (`useProfileLinksEnabled`, fail-closed): off, nothing is read. `active`
 * false (the Outreach screen on another tab) reads nothing either — the queries keep their cache keys, so the
 * Contacts tab paints from the cache when it comes back.
 */
export function useProfileLinkLeadRows(active = true): PeopleLeadRow[] {
  const enabled = useProfileLinksEnabled() && active;
  const list = useProfileLinksList(enabled);
  const links = useMemo(() => peopleLeadLinks(list.data?.links ?? []), [list.data]);
  const leads = useQueries({
    queries: links.map((link) => ({
      queryKey: queryKeys.profileLinkLeads(link.id),
      queryFn: () => profileLinksApi.leads(link.id),
      enabled,
      retry: false,
    })),
  });
  const leadsKey = leads.map((q) => q.dataUpdatedAt).join(',');
  return useMemo(
    () => (enabled ? peopleLeadRows(links, Object.fromEntries(links.map((link, i) => [link.id, leads[i]?.data?.leads]))) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, links, leadsKey],
  );
}

/**
 * "From your profile links" — the lead cards in People (design §8.3, the P4–P7 shared contract), above the
 * Outreach Contacts list. iOS and Android draw the same section under the same key.
 *
 * `rows` are the leads with NO contact in the list below (`matchLeadsToContacts(...).unmatched`): a lead who is
 * already a contact shows on that contact's row as "Wrote via /me/<handle>", never here as well.
 *
 * A row: the visitor's name (self-asserted), their phone (always shown, Q5), the link they came through
 * (its context title, or "Main profile") and when they first wrote. It opens the owner-side conversation
 * when the lead has one, else that link's People tab.
 *
 * It renders nothing with no rows — it is an addition to People, never a state of it, so the contacts below
 * never wait on it or wear its error.
 */
export function ProfileLinkLeadsSection({ rows }: { rows: readonly PeopleLeadRow[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <CategoryList>
      <CategoryListGroupHeader>{t('profileLinks.leads.section')}</CategoryListGroupHeader>
      {/* Needs-you first: the section answers "does anyone here need me" before "who came lately". */}
      {sortLeadRowsForSection(rows).map((row) => (
        <ProfileLeadRow key={row.key} row={row} />
      ))}
    </CategoryList>
  );
}

function ProfileLeadRow({ row }: { row: PeopleLeadRow }) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const rowSurface = useCategoryRowClass();
  const titleCap = useCategoryTitleClass();
  const { logPageClickBi } = useGenericBi();
  const { lead } = row;
  // A visitor whose account has no name is named by the link they came through, never "Unnamed contact".
  const name = leadDisplayName(row, (link) => t('profileLinks.leads.visitorFrom', { link }), t('profileLinks.main'));
  // Whether this one needs the owner, or the AI has it — and the visitor's latest words.
  const status = leadRowStatus(lead);
  const summary = lead.summary?.trim() ?? '';
  // The link path is LTR-isolated inside `leadSource` (it reordered to `me/ronny/` in he/ar).
  const meta = [lead.phone, leadSource(row, t('profileLinks.main'))].filter(Boolean).join(' · ');

  const open = () => {
    const target = leadOpenTarget(row);
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_profile_lead' },
      { additionalFields: { target: target.kind } },
    );
    if (target.kind === 'conversation') {
      const params = new URLSearchParams({ conversation: target.conversationId, chat: 'fullscreen' });
      window.location.assign(`${basePath}/?${params.toString()}`);
      return;
    }
    const route = `/profile-links/${encodeURIComponent(target.linkId)}?tab=people`;
    void router.push(route, `${basePath}${route}`);
  };

  return (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      disablePadding={!narrow}
      component="button"
      interactive
      className={`${narrow ? styles.rowCompact : styles.row}${rowSurface ? ` ${rowSurface}` : ''}`}
      onClick={open}
      data-testid="profile-lead-row"
    >
      <Avatar initials={avatarInitials(name)} size={narrow ? 40 : 32} color={avatarSlotFor(lead.id)} />
      <span className={narrow ? styles.rowRightCenter : styles.rowMain}>
        <span className={styles.rowMain}>
          <Typography variant="h4" color="primary" className={titleCap ? `${styles.rowName} ${titleCap}` : styles.rowName} dir="auto">
            {name}
          </Typography>
          {/* The Inbox's own stage dot, so "Needs you" reads the same colour here as on every outreach row. */}
          <span className={styles.inboxNameRow} data-testid="profile-lead-status">
            <StageDot stage={status.needsYou ? 'needsYou' : 'handling'} />
            <Typography variant="p3" color={status.needsYou ? 'primary' : 'secondary'} className={styles.rowSubtitle}>
              {t(status.key)}
            </Typography>
          </span>
          {summary ? (
            <Typography variant="p3" color="secondary" className={styles.rowSubtitle} dir="auto">
              {summary}
            </Typography>
          ) : null}
          <Typography variant="p3" color="secondary" className={styles.rowSubtitle} dir="auto">
            {narrow ? meta : [meta, longRelativeTime(lead.at)].filter(Boolean).join(' · ')}
          </Typography>
        </span>
        {narrow ? (
          <span className={styles.rowEnd}>
            <Typography variant="p3" color="secondary" className={styles.rowAge}>
              {shortRelativeTime(lead.at, undefined, t('contacts.time.now'))}
            </Typography>
            <ChevronRight size={24} className="dir-flip" aria-hidden />
          </span>
        ) : null}
      </span>
    </Card>
  );
}
